'use client'

import { useSignal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import styles from '@susu/headless-commerce/features/configurator/components/Navigation/Navigation.module.scss'
import { ClientSkeletonLoader } from '@susu/headless-commerce/features/configurator/components/Navigation/SkeletonLoader/ClientSkeletonLoader'
import { configuratorSignal } from '@susu/headless-commerce/features/configurator/signals/configurator'
import { trackFinalStepClick } from '@susu/headless-commerce/features/configurator/utils/trackFinalStepClick'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { useI18N } from '@susu/i18n/client'
import { isUndefined } from '@susu/undefined'
import classNames from 'classnames'
import { useCallback } from 'react'
import { trackOptionConfirm } from '../../utils/trackOptionConfirm'

export type ClientFinishButtonProps = {
  summaryURL: string
}

export const ClientFinishButton = ({ summaryURL }: ClientFinishButtonProps) => {
  useSignals()

  const locale = useSignal(useLocale())
  const pageType = useSignal(usePageType())
  const country = useSignal(useCountry())
  const configurator = configuratorSignal.value
  const isLoading = useSignal<boolean>(false)
  const { t } = useI18N()

  const handleClick = useCallback(async () => {
    isLoading.value = true
    configurator.handleFinish(summaryURL)

    if (isUndefined(configurator.selectedOption.value)) {
      return
    }

    await trackOptionConfirm({
      configurator,
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
      option: configurator.selectedOption.value,
    })

    await trackFinalStepClick({
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
      configurator,
      option: configurator.selectedOption.value,
    })
  }, [
    country.value,
    locale.value,
    pageType.value,
    isLoading,
    configurator,
    summaryURL,
  ])

  const navigationControlLastButtonClasses = classNames(
    styles['navigation-control--last-button'],
    {},
  )

  const navigationControlLastButtonTextClasses = classNames(
    styles['navigation-control__text'],
    'body-small-light',
  )

  return (
    <>
      <div className={styles['navigation-control__wrapper']}>
        <button
          type="button"
          className={navigationControlLastButtonClasses}
          onClick={handleClick}
          disabled={isLoading.value}
          data-testid="button_configurator_finish"
        >
          <span className={styles['navigation-control__icon']} />
          <span className={navigationControlLastButtonTextClasses}>
            {t('configurator.finish_look')}
          </span>
        </button>
      </div>
      {isLoading.value && <ClientSkeletonLoader />}
    </>
  )
}
